import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/confirmar-email")({
  head: () => ({
    meta: [
      { title: "Confirmar E-mail — Loja EG Elétrica" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ConfirmarEmailPage,
});

function ConfirmarEmailPage() {
  const navigate = useNavigate();
  const [checkingLink, setCheckingLink] = useState(true);
  const [validLink, setValidLink] = useState(false);
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");

  useEffect(() => {
    // O link do e-mail troca o token pela sessão automaticamente
    // (detectSessionInUrl); só precisamos confirmar que a sessão existe.
    supabase.auth.getSession().then(({ data }) => {
      setValidLink(!!data.session);
      setCheckingLink(false);
    });
  }, []);

  useEffect(() => {
    if (!validLink) return;
    const timer = setTimeout(() => navigate({ to: "/" }), 3000);
    return () => clearTimeout(timer);
  }, [validLink, navigate]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResending(true);
    setResendError("");
    setResendMessage("");
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/confirmar-email` },
    });
    setResending(false);
    if (error) {
      setResendError(
        isAuthRetryableFetchError(error)
          ? "Tivemos um problema técnico e não conseguimos reenviar agora. Tente novamente em alguns minutos."
          : "Não foi possível reenviar o e-mail agora. Verifique o e-mail informado e tente de novo.",
      );
      return;
    }
    setResendMessage("Se houver um cadastro pendente com esse e-mail, enviamos um novo link.");
  };

  if (checkingLink) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
      </div>
    );
  }

  if (validLink) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-eg-gold" />
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">E-mail confirmado!</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Sua conta foi ativada e você já está conectado. Vamos te levar para a loja...
        </p>
        <Link to="/" className="text-sm font-medium text-eg-goldtext hover:underline">
          Ir para a loja agora
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12 text-center">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">
        Link inválido ou expirado
      </h1>
      <p className="text-sm text-muted-foreground mb-6">
        Esse link de confirmação não é mais válido. Informe seu e-mail para receber um novo link.
      </p>
      <form
        onSubmit={handleResend}
        className="space-y-4 rounded-lg bg-white border border-border p-6 text-left"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">E-mail</span>
          <input
            type="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        {resendError && <p className="text-xs text-red-600">{resendError}</p>}
        {resendMessage && <p className="text-xs text-eg-goldtext">{resendMessage}</p>}
        <button
          type="submit"
          disabled={resending}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-gold px-4 py-2.5 font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {resending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
          Reenviar e-mail de confirmação
        </button>
        <div className="text-center text-xs text-muted-foreground">
          <Link to="/login" className="hover:text-eg-gold transition-colors">
            Já confirmou? Entrar
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}
